import { BookOpen, Building2, Layers } from "lucide-react";

export interface WorkspaceCoursePreview {
  id: number;
  uuid: string;
  title: string;
  description?: string | null;
  cover_image_url?: string | null;
  total_modules: number;
  total_lessons: number;
}

export interface JoinWorkspacePreview {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  is_default: boolean;
  courses: WorkspaceCoursePreview[];
}

interface WorkspaceCourseSelectorProps {
  workspaces: JoinWorkspacePreview[];
  selectedWorkspaceId: number | null;
  onSelect: (workspaceId: number) => void;
  appearance?: "dark" | "light";
}

export function WorkspaceCourseSelector({
  workspaces,
  selectedWorkspaceId,
  onSelect,
  appearance = "light",
}: WorkspaceCourseSelectorProps) {
  const selectedWorkspace = workspaces.find((workspace) => workspace.id === selectedWorkspaceId);
  const isDark = appearance === "dark";
  const headingClass = isDark ? "text-slate-300" : "text-slate-700";
  const selectedClass = isDark
    ? "border-indigo-500 bg-indigo-500/10 text-white"
    : "border-indigo-500 bg-indigo-50 text-slate-900";
  const unselectedClass = isDark
    ? "border-slate-700 bg-slate-950 text-slate-300 hover:bg-slate-800"
    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";
  const detailClass = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <section className="space-y-4" aria-label="Workspace selection">
      <div>
        <h3 className={`text-sm font-bold ${headingClass}`}>Choose a workspace</h3>
        <p className={`text-xs mt-1 ${detailClass}`}>Courses assigned to each workspace are shown after you select it.</p>
      </div>

      {workspaces.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Available workspaces">
          {workspaces.map((workspace) => {
            const isSelected = workspace.id === selectedWorkspaceId;
            return (
              <button
                key={workspace.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onSelect(workspace.id)}
                className={`flex min-w-0 items-start gap-3 rounded-xl border p-3 text-left transition ${isSelected ? selectedClass : unselectedClass}`}
              >
                <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{workspace.name}</span>
                  <span className={`mt-0.5 block text-xs ${detailClass}`}>
                    {workspace.courses.length} {workspace.courses.length === 1 ? "course" : "courses"}
                    {workspace.is_default ? " · Default" : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className={`rounded-xl border p-3 text-xs ${isDark ? "border-slate-700 text-slate-400" : "border-slate-200 text-slate-500"}`}>
          No workspaces are available yet. Please contact your organization administrator.
        </p>
      )}

      {selectedWorkspace && (
        <div className={`border-t pt-4 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <h4 className={`mb-3 flex items-center gap-2 text-xs font-bold ${headingClass}`}>
            <BookOpen className="h-3.5 w-3.5" />
            Courses in {selectedWorkspace.name}
          </h4>
          {selectedWorkspace.courses.length > 0 ? (
            <div className="space-y-2">
              {selectedWorkspace.courses.map((course) => (
                <div
                  key={course.id}
                  className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${isDark ? "border-slate-800 bg-slate-950/60" : "border-slate-200 bg-slate-50"}`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Layers className={`h-4 w-4 shrink-0 ${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
                    <div className="min-w-0">
                      <p className={`truncate text-sm font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>{course.title}</p>
                      <p className={`text-[11px] ${detailClass}`}>
                        {course.total_modules} Modules · {course.total_lessons} Lessons
                      </p>
                    </div>
                  </div>
                  <span className={`shrink-0 text-[11px] font-semibold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
                    Included
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-xs ${detailClass}`}>No courses are assigned to this workspace yet.</p>
          )}
        </div>
      )}
    </section>
  );
}
